const { mainConfigService } = require('../services/config');
const { FRIEND_PAY_DEFAULTS, MONTH_NAMES } = require('../constant/friendPayConstants');
const { $t } = require('../translate');

/**
 * Extracts and formats friend pay configuration from mainConfigService
 * @returns {Object} Formatted friend pay configuration
 */
exports.getFriendPayConfig = function() {
  const setupPreload = mainConfigService.get('setupPreload') || {};
  const billing = setupPreload.billing || {};
  const shareTextConfig = mainConfigService.get('shareText') || {};
  const currentLang = mainConfigService.get('lang') || 'en';

  // Extract and format amount
  const amount = billing.amount?.display || billing.amount?.value || FRIEND_PAY_DEFAULTS.FALLBACK_AMOUNT;
  const currency = billing.amount?.currency_code || FRIEND_PAY_DEFAULTS.FALLBACK_CURRENCY;
  const orderTotal = amount.includes(currency) ? amount : `${currency} ${amount}`;

  // Extract share text
  const defaultMessage = currentLang === 'ar'
    ? FRIEND_PAY_DEFAULTS.SHARE_MESSAGE_AR
    : FRIEND_PAY_DEFAULTS.SHARE_MESSAGE_EN;
  const shareText = shareTextConfig[currentLang] || defaultMessage;

  return {
    setupPreload,
    billing,
    amount,
    currency,
    orderTotal,
    shareText,
    currentLang,
    expirationTime: setupPreload.expiration_time
  };
};

/**
 * Generates icon HTML with consistent sizing and attributes
 * @param {string} svgUrl - URL to the SVG icon
 * @param {string} alt - Alt text for the icon
 * @param {number} size - Icon size in pixels
 * @returns {string} Icon HTML string
 */
exports.createIconHTML = function(svgUrl, alt, size) {
  return `<img src="${svgUrl}" alt="${alt}" width="${size}" height="${size}" />`;
};

/**
 * Generates a share button icon with background wrapper
 * @param {Object} platform - Platform configuration from SHARE_PLATFORMS
 * @param {Object} FRIEND_PAY_SVG - SVG path object
 * @param {number} iconSize - Icon size (default: 28)
 * @returns {string} Share button HTML
 */
exports.createShareButtonHTML = function(platform, FRIEND_PAY_SVG, iconSize = 28) {
  return `
    <button class="ottu__sdk-friend-pay-share-btn" data-share-type="${platform.type}" data-testid="ottu-sdk-friendpay-share-${platform.type}">
      <div class="ottu__sdk-friend-pay-share-icon ${platform.bgClass}">
        <img src="${FRIEND_PAY_SVG[platform.svgKey]}" alt="${platform.label}" width="${iconSize}" height="${iconSize}" />
      </div>
      <span class="ottu__sdk-friend-pay-share-label ottu__sdk-friend-pay-share-label-theme">${platform.label}</span>
    </button>
  `;
};

/**
 * Calculate time components from milliseconds
 * @param {number} remainingMs - Milliseconds remaining
 * @returns {Object} { days, hours, minutes, seconds, totalSeconds }
 */
exports.calculateTimeRemaining = function(remainingMs) {
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, totalSeconds };
};

/**
 * Format time as HH:MM:SS for countdown display
 * @param {Object} timeComponents - Object from calculateTimeRemaining
 * @returns {string} Formatted time string (e.g., "05:58:43")
 */
exports.formatCountdownTime = function(timeComponents) {
  const { hours, minutes, seconds } = timeComponents;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Format days and hours for display (e.g., "2 days 5 hours")
 * @param {number} days - Number of days
 * @param {number} hours - Number of hours
 * @param {string} lang - Language code ('en' or 'ar')
 * @returns {string} Formatted string
 */
exports.formatDaysHours = function(days, hours, lang) {

  if (lang === 'ar') {
    const dayText = days === 1 ? $t('day') : days === 2 ? $t('days_two') : $t('days');
    const hourText = hours === 1 ? $t('hour') : hours === 2 ? $t('hours_two') : $t('hours');
    return `${days} ${dayText} ${hours} ${hourText}`;
  } else {
    const dayText = days === 1 ? $t('day') : $t('days');
    const hourText = hours === 1 ? $t('hour') : $t('hours');
    return `${days} ${dayText} ${hours} ${hourText}`;
  }
};

/**
 * Format expiration date (e.g., "Dec 18, 10:32 AM" or "18 ديسمبر، 10:32 ص")
 * @param {Date} expirationDate - The expiration date object
 * @param {string} lang - Language code ('en' or 'ar')
 * @returns {string} Formatted date string
 */
exports.formatExpirationDate = function(expirationDate, lang) {
  const day = expirationDate.getDate();
  const month = expirationDate.getMonth();
  const hours = expirationDate.getHours();
  const minutes = expirationDate.getMinutes();

  const isPM = hours >= 12;
  const hour12 = hours % 12 || 12;
  const minuteStr = String(minutes).padStart(2, '0');

  if (lang === 'ar') {
    const monthName = MONTH_NAMES.ar[month];
    const amPm = isPM ? 'م' : 'ص';
    return `${day} ${monthName}، ${hour12}:${minuteStr} ${amPm}`;
  } else {
    const monthName = MONTH_NAMES.en[month];
    const amPm = isPM ? 'PM' : 'AM';
    return `${monthName} ${day}, ${hour12}:${minuteStr} ${amPm}`;
  }
};

/**
 * Determine timer state based on remaining time
 * @param {number} remainingMs - Milliseconds remaining
 * @returns {string} 'expired' | 'less_than_day' | 'more_than_day'
 */
exports.getTimerState = function(remainingMs) {
  if (remainingMs <= 0) return 'expired';
  const oneDayMs = 86400000; // 24 * 60 * 60 * 1000
  return remainingMs < oneDayMs ? 'less_than_day' : 'more_than_day';
};
